import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { authFetch } from '../../utils/auth-fetch';
import { AlertTriangle, ChevronDown, ChevronRight, Layers, RefreshCw, RotateCcw } from 'lucide-react';
import { Absent } from './opsConsoleAbsent';
import { AlertsTable, type OpsAlert } from './OpsAlertsPanel';

/**
 * Ops Console — a merchant's batches + the operator re-run (PRD vault offering/2026-10-08-shopify-pipeline-batches-prd
 * §6.4). Read through GET /ops-console/:platform/merchants/:connectionId/batches — the SAME module the merchant's
 * Pipeline screen reads, so these are the merchant's numbers, split: what the merchant sees as Pending is shown here as
 * In flight + Failed (ours).
 *
 * THE CONSOLE'S FIRST ACTION: "Re-run failed products" on a batch with Failed (ours) > 0, behind a confirmation that
 * lists exactly what will be re-run. The server re-runs only the batch's OUR failures with no later successful
 * re-run — the list here is shown for confirmation, never sent — through the merchant's own /generate. Its audit
 * record is the re-run's own batch row, shown below the batch it re-ran ("Re-run of #n by …").
 *
 * No polling and no cache (§5.5): a Refresh button re-fetches.
 */

type Group = 'completed' | 'in_flight' | 'needs_more_detail' | 'plan_limit_reached' | 'ours';

interface OpsBatchProduct {
  child_id: string;
  pfi_id: string | null;
  product_name: string | null;
  platform_product_id: string | null;
  /** false when the product has left the store's active catalogue — a re-run skips it. */
  active: boolean;
  status: string;
  group: Group;
  owner_class: string | null;
  alert_class: string | null;
  verdict: string | null;
  verdict_title: string | null;
  error_message: string | null;
  resolved_by_rerun: { batch_id: string; original_group: Group; original_error_message: string | null } | null;
}

interface OpsBatchAlert {
  id: string;
  kind: string;
  severity: string;
  verdict_label: string | null;
  summary: string | null;
  last_seen: string;
}

interface OpsBatchCounts {
  total: number;
  completed: number;
  in_flight: number;
  failed_ours: number;
  needs_more_detail: number;
  plan_limit_reached: number;
}

export interface OpsRerun extends OpsBatchCounts {
  id: string;
  rerun_of: string;
  rerun_of_number: number;
  operator: { id: string | null; name: string | null; email: string | null };
  started_at: string;
  completed_at: string | null;
  status: 'running' | 'done';
  category: string | null;
  products: OpsBatchProduct[];
  alerts: OpsBatchAlert[] | null;
}

export interface OpsBatch extends OpsBatchCounts {
  id: string;
  number: number;
  started_at: string;
  completed_at: string | null;
  status: 'running' | 'done';
  category: string | null;
  rerunnable_count: number;
  products: OpsBatchProduct[];
  reruns: OpsRerun[];
  alerts: OpsBatchAlert[] | null;
}

interface BatchesResponse {
  batches: OpsBatch[];
  next_cursor: string | null;
  alerts_available: boolean;
}

const GROUP_LABEL: Record<Group, string> = {
  completed: 'Completed',
  in_flight: 'In flight',
  ours: 'Failed (ours)',
  needs_more_detail: 'Needs more detail',
  plan_limit_reached: 'Plan limit',
};
const GROUP_CLASS: Record<Group, string> = {
  completed: 'text-green-700',
  in_flight: 'text-slate-600',
  ours: 'text-red-600',
  needs_more_detail: 'text-amber-700',
  plan_limit_reached: 'text-amber-700',
};

const fmtDateTime = (v: string | null) => (v ? new Date(v).toLocaleString() : null);
const operatorName = (o: OpsRerun['operator']) => o.name || o.email || o.id || 'unknown operator';
const isAbort = (err: unknown) =>
  err instanceof DOMException ? err.name === 'AbortError' : (err as { name?: string })?.name === 'AbortError';

async function readJson(res: Response) {
  return res.json().catch(() => ({}));
}

/**
 * The re-run POST's body, read STRICTLY (review #7045 r3): an empty or unparseable body is an error that says so,
 * never `{}` — a `{}` on a 2xx read as "queued" with nothing behind it.
 */
/**
 * The answers that CONFIRM nothing was submitted (review #7045 r5). Every one is returned before /generate inserts
 * anything, by construction:
 *   - the re-run route (backend-merged/routes/ops-console.js operatorRerun): 400 is_rerun; 404 not_found / merchant
 *     connection not found / unknown platform / no batches source; 409 rerun_submitting, nothing_to_rerun,
 *     rerun_running, batch_running;
 *   - /generate (routes/shopify-embedded.js generateHandler), all before its parent insert: 400 product_ids missing /
 *     over the batch cap / invalid ids / plan without AEO; 402 subscription_not_entitled, monthly_quota_exhausted;
 *     403 products not this store's; 404 store not connected; 409 subscription_pending, inconsistent_billing_state,
 *     generation_busy, already_in_progress; 422 category_required_for_generation;
 *   - auth: 401, 403 (super-admin gate).
 * Anything else — every 5xx, a gateway timeout, an unrecognised status — can follow partial persistence (the parent
 * and children are separate autocommitted inserts) or hide a submission still running, so it is NOT a refusal.
 */
const CONFIRMED_REFUSAL_STATUSES = new Set([400, 401, 402, 403, 404, 409, 422]);

async function readJsonStrict(res: Response): Promise<Record<string, unknown>> {
  const text = await res.text();
  if (!text || !text.trim()) throw new Error(`empty response body (HTTP ${res.status})`);
  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== 'object') throw new Error('not a JSON object');
    return parsed as Record<string, unknown>;
  } catch (err) {
    throw new Error(`unreadable response (HTTP ${res.status}): ${text.slice(0, 200)}`);
  }
}

function ProductsTable({ products }: { products: OpsBatchProduct[] }) {
  if (products.length === 0) return <div className="text-xs text-muted-foreground">This batch has no products.</div>;
  return (
    <table className="w-full text-xs border-collapse" data-testid="ops-batch-products">
      <thead>
        <tr className="text-left text-muted-foreground">
          <th className="pr-2">Product</th><th className="pr-2">Group</th><th className="pr-2">Class</th><th className="pr-2">Verdict</th><th>error_message</th>
        </tr>
      </thead>
      <tbody>
        {products.map((p) => (
          <tr key={p.child_id} className="align-top border-t" data-testid="ops-batch-product" data-group={p.group}>
            <td className="pr-2">
              {p.product_name ?? <Absent why="No product name recorded" />}
              {p.pfi_id && <div className="text-muted-foreground break-all">{p.pfi_id}</div>}
            </td>
            <td className={`pr-2 whitespace-nowrap ${GROUP_CLASS[p.group]}`}>
              {GROUP_LABEL[p.group]}
              {p.resolved_by_rerun && (
                <div className="text-muted-foreground" data-testid="ops-resolved-by-rerun">
                  by re-run (was {GROUP_LABEL[p.resolved_by_rerun.original_group]})
                </div>
              )}
            </td>
            <td className="pr-2">{p.owner_class ?? <Absent why="Not a failure" />}</td>
            <td className="pr-2">{p.verdict ? <><div className="font-medium">{p.verdict}</div><div className="text-muted-foreground">{p.verdict_title}</div></> : <Absent why="Not a failure" />}</td>
            <td className="whitespace-pre-wrap break-words">
              {p.error_message ?? <Absent why="No error message recorded" />}
              {p.resolved_by_rerun?.original_error_message && (
                <div className="text-muted-foreground">originally: {p.resolved_by_rerun.original_error_message}</div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

type OpenedAlert =
  | { kind: 'loading' }
  | { kind: 'shown'; alert: OpsAlert }
  | { kind: 'missing' }
  | { kind: 'unavailable'; message: string }
  | { kind: 'error'; message: string };

/**
 * Load ONE alert explicitly (review #7045 r6) — through the merchant's existing alerts read, with its window narrowed to
 * a second either side of this alert's last_seen and filtered to its kind and severity, following next_cursor until the
 * row with this id is found. It never depends on what the Alerts panel below happened to load (its default window is
 * 30 days and its first page 100 rows), so an older or paginated alert opens too.
 */
async function loadOneAlert(platform: string, connectionId: string, a: OpsBatchAlert): Promise<OpenedAlert> {
  const seen = new Date(a.last_seen).getTime();
  if (Number.isNaN(seen)) return { kind: 'error', message: `the alert's last_seen is not a date: ${JSON.stringify(a.last_seen)}` };
  const q = new URLSearchParams({
    since: new Date(seen - 1000).toISOString(),
    until: new Date(seen + 1000).toISOString(),
    kind: a.kind,
    severity: a.severity,
  });
  const path = `/ops-console/${platform}/merchants/${encodeURIComponent(connectionId)}/alerts`;
  let cursor: string | null = null;
  for (;;) {
    const qs = new URLSearchParams(q);
    if (cursor) qs.set('cursor', cursor);
    const res = await authFetch(`${path}?${qs.toString()}`);
    const body = await readJsonStrict(res);
    if (res.status === 503 && body.code === 'alerts_unavailable') {
      return { kind: 'unavailable', message: String(body.message || 'Alert table unavailable') };
    }
    if (!res.ok) return { kind: 'error', message: String(body.message || body.error || `HTTP ${res.status}`) };
    if (!Array.isArray(body.alerts)) return { kind: 'error', message: 'Alerts payload is missing alerts[]' };
    const found = (body.alerts as OpsAlert[]).find((x) => x.id === a.id);
    if (found) return { kind: 'shown', alert: found };
    cursor = typeof body.next_cursor === 'string' ? body.next_cursor : null;
    if (!cursor) return { kind: 'missing' };
  }
}

function AlertLinks({ alerts, platform, connectionId }: { alerts: OpsBatchAlert[] | null; platform: string; connectionId: string }) {
  const [opened, setOpened] = useState<Record<string, OpenedAlert>>({});
  if (alerts === null) return <div className="text-xs text-amber-800">Alert table unavailable — this is not "no alerts".</div>;
  if (alerts.length === 0) return <div className="text-xs text-muted-foreground">No ops alert recorded for this batch.</div>;
  const open = async (a: OpsBatchAlert) => {
    if (opened[a.id]?.kind === 'shown' || opened[a.id]?.kind === 'loading') return;
    setOpened((prev) => ({ ...prev, [a.id]: { kind: 'loading' } }));
    let next: OpenedAlert;
    try {
      next = await loadOneAlert(platform, connectionId, a);
    } catch (err) {
      next = { kind: 'error', message: err instanceof Error ? err.message : String(err) };
    }
    setOpened((prev) => ({ ...prev, [a.id]: next }));
  };
  return (
    <ul className="text-xs space-y-0.5" data-testid="ops-batch-alerts">
      {alerts.map((a) => {
        const o = opened[a.id];
        return (
          <li key={a.id}>
            <button type="button" className="text-blue-600 underline" data-testid="ops-batch-alert-open" onClick={() => { void open(a); }}>
              {a.severity} · {a.kind}
            </button>
            {a.verdict_label ? ` — ${a.verdict_label}` : ''}{a.summary ? ` · ${a.summary}` : ''}
            <span className="text-muted-foreground"> ({fmtDateTime(a.last_seen)})</span>
            {o?.kind === 'loading' && <div className="text-muted-foreground">Loading the alert…</div>}
            {o?.kind === 'missing' && (
              <div className="text-amber-800" data-testid="ops-batch-alert-missing">This alert is no longer in the alerts table at its recorded time.</div>
            )}
            {o?.kind === 'unavailable' && <div className="text-amber-800">Alert table unavailable — {o.message}</div>}
            {o?.kind === 'error' && <div className="text-red-600" data-testid="ops-batch-alert-error">The alert failed to load: {o.message}</div>}
            {o?.kind === 'shown' && (
              <div data-testid="ops-batch-alert-opened" onClick={(e) => e.stopPropagation()}>
                <AlertsTable alerts={[o.alert]} showShop={false} initiallyExpanded={[o.alert.id]} />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function CountCells({ b }: { b: OpsBatchCounts }) {
  return (
    <>
      <TableCell className="text-right">{b.total}</TableCell>
      <TableCell className="text-right">{b.completed}</TableCell>
      <TableCell className="text-right">{b.in_flight}</TableCell>
      <TableCell className={`text-right ${b.failed_ours > 0 ? 'text-red-600 font-semibold' : ''}`}>{b.failed_ours}</TableCell>
      <TableCell className="text-right">{b.needs_more_detail}</TableCell>
      <TableCell className="text-right">{b.plan_limit_reached}</TableCell>
    </>
  );
}

type LoadState = { kind: 'loading' } | { kind: 'ready' } | { kind: 'error'; message: string };
type RerunState =
  | { kind: 'idle' }
  | { kind: 'confirm'; batch: OpsBatch }
  | { kind: 'submitting'; batch: OpsBatch }
  | { kind: 'done'; batchNumber: number; jobId: string | null; message: string }
  | { kind: 'refused'; batchNumber: number; message: string }
  // A 2xx whose body is not the queued contract: the re-run MAY have started — say so, and do not claim either way.
  | { kind: 'unknown'; batchNumber: number; message: string };

export default function OpsBatchesPanel({ platform, connectionId }: { platform: string; connectionId: string }) {
  const [batches, setBatches] = useState<OpsBatch[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [rerun, setRerun] = useState<RerunState>({ kind: 'idle' });
  const abortRef = useRef<AbortController | null>(null);
  const base = `/ops-console/${platform}/merchants/${encodeURIComponent(connectionId)}/batches`;

  const load = useCallback(async (append: boolean, before: string | null) => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    if (!append) setState({ kind: 'loading' });
    try {
      const res = await authFetch(before ? `${base}?before=${encodeURIComponent(before)}` : base, { signal: ctrl.signal });
      const body = await readJson(res);
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      // ⛔ No silent fallback: a payload without the list is an error, never "no batches".
      if (!Array.isArray(body?.batches)) throw new Error('Batches payload is missing batches[] — the admin build and the backend are probably out of step.');
      if (ctrl.signal.aborted) return;
      const data = body as BatchesResponse;
      setBatches((prev) => (append ? [...prev, ...data.batches] : data.batches));
      setCursor(data.next_cursor);
      setState({ kind: 'ready' });
    } catch (err) {
      if (isAbort(err)) return;
      setState({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
      if (!append) setBatches([]);
    }
  }, [base]);

  useEffect(() => { load(false, null); }, [load]);
  useEffect(() => () => abortRef.current?.abort(), []);

  const toggle = (id: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const submitRerun = async (batch: OpsBatch) => {
    setRerun({ kind: 'submitting', batch });
    try {
      // The body is deliberately empty: the server decides which products to re-run.
      const res = await authFetch(`${base}/${encodeURIComponent(batch.id)}/rerun`, { method: 'POST', body: JSON.stringify({}) });
      let body: Record<string, unknown>;
      try {
        body = await readJsonStrict(res);
      } catch (err) {
        const why = err instanceof Error ? err.message : String(err);
        // An answer we cannot read is not a refusal we can confirm — whatever its status. The server keeps
        // submitting after a broken response (review #7045 r4), so the batch may exist: say we do not know.
        setRerun({ kind: 'unknown', batchNumber: batch.number, message: why });
        load(false, null);
        return;
      }
      if (res.ok) {
        // Success is /generate's queued contract — status 'queued' AND a batch id — nothing less.
        const jobId = typeof body.job_id === 'string' && body.job_id ? body.job_id : null;
        if (body.status !== 'queued' || !jobId) {
          setRerun({
            kind: 'unknown',
            batchNumber: batch.number,
            message: `the server answered HTTP ${res.status} without a queued batch (status ${JSON.stringify(body.status ?? null)}, job_id ${JSON.stringify(body.job_id ?? null)})`,
          });
        } else {
          const n = typeof body.accepted_count === 'number' ? body.accepted_count : (typeof body.total === 'number' ? body.total : null);
          const blocked = typeof body.quota_blocked_count === 'number' ? body.quota_blocked_count : 0;
          setRerun({
            kind: 'done',
            batchNumber: batch.number,
            jobId,
            message: `Re-run of batch #${batch.number} queued${n !== null ? ` — ${n} product${n === 1 ? '' : 's'}` : ''}${blocked > 0 ? `, ${blocked} blocked by the merchant's plan limit` : ''}.`,
          });
        }
      } else if (CONFIRMED_REFUSAL_STATUSES.has(res.status)) {
        setRerun({ kind: 'refused', batchNumber: batch.number, message: String(body.error || body.message || `HTTP ${res.status}`) });
      } else {
        setRerun({
          kind: 'unknown',
          batchNumber: batch.number,
          message: `the server answered HTTP ${res.status}${body.error || body.message ? ` (${String(body.error || body.message)})` : ''}`,
        });
      }
    } catch (err) {
      // A transport failure after the request was sent: the server may already have inserted the re-run (submission
      // continues after a disconnect, review #7045 r2/r4) — unknown, never "not started".
      setRerun({ kind: 'unknown', batchNumber: batch.number, message: `no answer from the server (${err instanceof Error ? err.message : String(err)})` });
    }
    load(false, null);
  };

  // The server re-runs only OUR failures still in the active catalogue (review #7045 r2); the rest are named as skipped.
  const rerunnableProducts = (b: OpsBatch) => b.products.filter((p) => p.group === 'ours' && p.active);
  const inactiveOurs = (b: OpsBatch) => b.products.filter((p) => p.group === 'ours' && !p.active);

  return (
    <Card data-testid="merchant-batches">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-sm flex items-center gap-2"><Layers className="h-4 w-4" />Batches</CardTitle>
            <CardDescription>
              The merchant's Generate batches, newest first — the numbers the merchant sees on their Pipeline screen, with
              their Pending split into <b>In flight</b> and <b>Failed (ours)</b>. A batch re-run from here appears under the
              batch it re-ran.
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={() => load(false, null)} data-testid="batches-refresh">
            <RefreshCw className="h-3 w-3 mr-2" />Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {rerun.kind === 'confirm' || rerun.kind === 'submitting' ? (
          <div role="alertdialog" aria-labelledby="ops-rerun-title" aria-describedby="ops-rerun-desc" className="border border-amber-300 bg-amber-50 rounded p-3 space-y-2" data-testid="rerun-confirm">
            <div id="ops-rerun-title" className="text-sm font-semibold">Re-run failed products of batch #{rerun.batch.number}?</div>
            <div id="ops-rerun-desc" className="text-xs">
              These {rerunnableProducts(rerun.batch).length} products failed on our side and have no later successful re-run.
              They run through the merchant's own Generate — the same plan limit, e-mails and alerts as the merchant's click.
              Any of them the store has generated since (in another batch or re-run) is skipped by the server.
            </div>
            <ul className="text-xs list-disc ml-5" data-testid="rerun-confirm-products">
              {rerunnableProducts(rerun.batch).map((p) => <li key={p.child_id}>{p.product_name ?? p.pfi_id}</li>)}
            </ul>
            {inactiveOurs(rerun.batch).length > 0 && (
              <div className="text-xs text-amber-900" data-testid="rerun-confirm-skipped-inactive">
                Skipped — no longer in the store's active catalogue: {inactiveOurs(rerun.batch).map((p) => p.product_name ?? p.pfi_id).join(', ')}.
              </div>
            )}
            <div className="flex gap-2">
              <Button size="sm" onClick={() => submitRerun(rerun.batch)} disabled={rerun.kind === 'submitting'} data-testid="rerun-confirm-yes">
                {rerun.kind === 'submitting' ? 'Re-running…' : 'Re-run these products'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setRerun({ kind: 'idle' })} disabled={rerun.kind === 'submitting'}>Cancel</Button>
            </div>
          </div>
        ) : null}
        {rerun.kind === 'done' && <p className="text-sm text-green-700" role="status" data-testid="rerun-done">{rerun.message}</p>}
        {rerun.kind === 'unknown' && (
          <p className="text-sm text-amber-800 flex items-center gap-1" role="alert" data-testid="rerun-unknown">
            <AlertTriangle className="h-4 w-4" /> Re-run of batch #{rerun.batchNumber}: the server's answer could not be confirmed ({rerun.message}). Refresh to see whether it started.
          </p>
        )}
        {rerun.kind === 'refused' && (
          <p className="text-sm text-red-600 flex items-center gap-1" role="alert" data-testid="rerun-refused">
            <AlertTriangle className="h-4 w-4" /> Re-run of batch #{rerun.batchNumber} not started: {rerun.message}
          </p>
        )}

        {state.kind === 'loading' && <p className="text-sm text-muted-foreground">Loading batches…</p>}
        {state.kind === 'error' && (
          <p className="text-sm text-red-600 flex items-center gap-1" data-testid="batches-error">
            <AlertTriangle className="h-4 w-4" /> Batches failed to load: {state.message}
          </p>
        )}
        {state.kind === 'ready' && batches.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="batches-empty">This merchant has not started a batch.</p>
        )}
        {batches.length > 0 && (
          <Table data-testid="batches-table">
            <TableHeader>
              <TableRow>
                <TableHead />
                <TableHead>Batch</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Completed</TableHead>
                <TableHead className="text-right">In flight</TableHead>
                <TableHead className="text-right">Failed (ours)</TableHead>
                <TableHead className="text-right">Needs more detail</TableHead>
                <TableHead className="text-right">Plan limit</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {batches.map((b) => {
                const open = expanded.has(b.id);
                return (
                  <React.Fragment key={b.id}>
                    <TableRow data-testid="ops-batch-row" data-batch-id={b.id} className="cursor-pointer" onClick={() => toggle(b.id)}>
                      <TableCell>{open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        <b>#{b.number}</b>{b.status === 'running' && <span className="ml-1 text-violet-700">in progress</span>}
                        <div className="text-muted-foreground">{fmtDateTime(b.started_at)}{b.category ? ` · ${b.category}` : ''}</div>
                      </TableCell>
                      <CountCells b={b} />
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {b.rerunnable_count > 0 && (
                          <Button size="sm" variant="outline" data-testid="rerun-button"
                            disabled={rerun.kind === 'submitting'}
                            onClick={() => setRerun({ kind: 'confirm', batch: b })}>
                            <RotateCcw className="h-3 w-3 mr-1" />Re-run failed products
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {open && (
                      <TableRow data-testid="ops-batch-expanded" data-batch-id={b.id}>
                        <TableCell />
                        <TableCell colSpan={8} className="space-y-2">
                          <ProductsTable products={b.products} />
                          <AlertLinks alerts={b.alerts} platform={platform} connectionId={connectionId} />
                        </TableCell>
                      </TableRow>
                    )}
                    {b.reruns.map((r) => (
                      <React.Fragment key={r.id}>
                        <TableRow data-testid="ops-rerun-row" data-batch-id={r.id} className="cursor-pointer bg-muted/40" onClick={() => toggle(r.id)}>
                          <TableCell>{expanded.has(r.id) ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}</TableCell>
                          <TableCell className="text-xs">
                            <span data-testid="ops-rerun-label">Re-run of #{r.rerun_of_number} by {operatorName(r.operator)}</span>
                            {r.status === 'running' && <span className="ml-1 text-violet-700">in progress</span>}
                            <div className="text-muted-foreground">{fmtDateTime(r.started_at)}</div>
                          </TableCell>
                          <CountCells b={r} />
                          <TableCell />
                        </TableRow>
                        {expanded.has(r.id) && (
                          <TableRow data-testid="ops-batch-expanded" data-batch-id={r.id}>
                            <TableCell />
                            <TableCell colSpan={8} className="space-y-2">
                              <ProductsTable products={r.products} />
                              <AlertLinks alerts={r.alerts} platform={platform} connectionId={connectionId} />
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
        {state.kind === 'ready' && cursor && (
          <Button size="sm" variant="outline" onClick={() => load(true, cursor)} data-testid="batches-more">Show older batches</Button>
        )}
      </CardContent>
    </Card>
  );
}
